import TooltipIcon from '@material-ui/core/TooltipIcon';
import FormCheckbox from 'cl-form/FormCheckbox';
import TypedFormField from 'cl-form/TypedFormField';
import {ValueType} from 'cl-form/validation/validators';
import {getColumnDisplayName} from 'cl-table/VirtualizedHelpers';
import * as React from 'react';
import {useEffect, useState, useMemo} from 'react';
import Divider from '@material-ui/core/Divider';
import {Omit} from '@material-ui/core';
import FormDialog, {Fields, SpecificFormDialogProps} from 'cl-dialogs/FormDialog';
import FormRadio from 'cl-form/FormRadio';
import {DISPLAY_TYPES} from 'cl-form/FormSections/consts';
import FormSelect from 'cl-form/FormSelect';
import GridFormHeader from 'cl-form/GridFormHeader';
import GridFormLabel from 'cl-form/GridFormLabel';
import {FormSelectOption, FormSelectOptions} from 'cl-form/utils/orderByUtils';
import {ColumnsInfo, TableState} from 'cl-table/redux/actions';
import * as _ from 'lodash';
import {formValueSelector} from 'redux-form';
import {StoreState} from '../../../../App/initialStore';
import {getProductsColumnsData} from '../../../../MyAccount/Inventory/utils';
import {ACTION_DISPLAY_TYPES} from '../../../ActionsManager/ActionsManagerConsts';
import {GENERAL_ACTIONS_TYPES} from '../../../ActionsManager/ActionsTypes';
import GeneralActions from '../../../ActionsManager/GeneralActions/GeneralActions';
import {
  EMPTY_VALUE,
  MappableProductProperty,
  valueToFieldName,
} from '../../../ActionsManager/GeneralActions/GeneralActionsHelpers';
import connectWithRouter from '../../redux/connectWithRouter';
import EbayTemplateSelect from '../DialogComponents/EbayTemplateSelect';

export enum MappingType {
  Empty = 'empty',
  Single = 'single',
  DirectMapping = 'direct mapping',
  ValueMapping = 'value mapping',
}

const getFormValues = formValueSelector(GENERAL_ACTIONS_TYPES.MAP_PRODUCT_PROPERTY);

export interface EditColumnFormData {
  value?: ValueType;
  selectedMappingType: string;
  mapValues: boolean;
  mappedAttribute?: string;
}

interface InnerMapProductValuesProps extends Partial<EditColumnFormData> {
  currentMapping: MappableProductProperty;
  propertyName?: string;
  tableName?: string;
  columnInfo?: ColumnsInfo;
  data?: TableState['data'];
  marketplaceName: string;
}

export type EditColumnDialogProps = SpecificFormDialogProps<EditColumnFormData, InnerMapProductValuesProps>;

function EditColumnDialog({
  selectedMappingType,
  propertyName,
  columnInfo,
  mappedAttribute,
  tableName,
  mapValues,
  data,
  currentMapping,
  marketplaceName,
  ...props
}: Omit<EditColumnDialogProps, 'formName'>) {
  const columnDisplayName = getColumnDisplayName(propertyName, columnInfo);
  const selectionOptions = [
    {
      label: (
        <React.Fragment>
          Single value{' '}
          <TooltipIcon
            title={`Enter a value for the '${columnDisplayName}' field that will be applied to all listings.`}
            variant="inline"
          />
        </React.Fragment>
      ),
      value: MappingType.Single,
    },
    {
      label: (
        <React.Fragment>
          Map from product attribute{' '}
          <TooltipIcon
            title={`Select a product attribute. This attribute will be used to fill the listing's '${columnDisplayName}' field from the matching product`}
            variant="inline"
          />
        </React.Fragment>
      ),
      value: MappingType.DirectMapping,
    },
  ];
  const [mappedAttributeOptions, setMappedAttributeOptions] = useState<FormSelectOptions>(null);
  const productValues = useMemo<string[]>(() => {
    const productValues = _.sortBy(
      _.uniq(_.map(data.toArray(), rowData => _.get(rowData._source, mappedAttribute) || EMPTY_VALUE))
    );
    if (_.includes(productValues, EMPTY_VALUE)) {
      _.pull(productValues, EMPTY_VALUE);
      productValues.push(EMPTY_VALUE);
    }
    return productValues;
  }, [data, mappedAttribute]);

  useEffect(() => {
    async function effect() {
      const mappedAttributeOptions = _.map<ColumnsInfo, FormSelectOption>(
        _.omitBy(await getProductsColumnsData(), 'hidden'),
        (productColumn, productColumnName): FormSelectOption => {
          const displayName = getColumnDisplayName(productColumnName, productColumn);
          return {
            value: productColumnName,
            label: displayName,
          };
        }
      );
      setMappedAttributeOptions(mappedAttributeOptions);
    }
    // noinspection JSIgnoredPromiseFromCall
    effect();
  }, []);

  const mappingEnabled = columnInfo.type === 'select' && !columnInfo.mappingDisabled;
  const initialValues = useMemo<EditColumnFormData>(() => {
    if (!currentMapping) {
      return {
        selectedMappingType: selectionOptions[0].value,
        mapValues: mappingEnabled,
      };
    } else if (currentMapping.mappingType === MappingType.Empty || currentMapping.mappingType === MappingType.Single) {
      return {
        selectedMappingType: selectionOptions[0].value,
        mapValues: mappingEnabled,
        value: currentMapping.mappingType === MappingType.Empty ? undefined : currentMapping.name,
      };
    } else {
      return {
        selectedMappingType: selectionOptions[1].value,
        mapValues: currentMapping.mappingType === MappingType.ValueMapping,
        mappedAttribute: currentMapping.name,
        ...currentMapping.valueMapping,
      };
    }
  }, []);

  const singleValueMapping = selectedMappingType === selectionOptions[0].value;
  let mapping = [];
  const renderMappingFields = !singleValueMapping && mapValues && !_.isEmpty(mappedAttribute) && mappingEnabled;
  if (renderMappingFields) {
    mapping = [
      {
        component: GridFormHeader,
        name: 'header',
        indent: 1,
        props: {
          leftText: "Product's attribute values:",
          rightText: `Available ${marketplaceName} values:`,
        },
      },
    ];
    _.forEach(productValues, productValue => {
      mapping.push({
        component: GridFormLabel,
        label: productValue,
        name: valueToFieldName(productValue),
        indent: 1,
        props: {
          inputComponent: columnInfo.displayType === DISPLAY_TYPES.TEMPLATE_SELECTOR ? EbayTemplateSelect : FormSelect,
          options: columnInfo.options,
        },
      });
    });
  }

  const fields: Fields<EditColumnFormData> = [
    {component: FormRadio, name: 'selectedMappingType', key: 0, options: [selectionOptions[0]]},
    singleValueMapping
      ? {
          component: columnInfo.displayType === DISPLAY_TYPES.TEMPLATE_SELECTOR ? EbayTemplateSelect : TypedFormField,
          name: 'value',
          indent: 1,
          placeholder: columnInfo.type !== 'select' ? 'Enter value...' : undefined,
          props: _.pick(columnInfo, [
            'type',
            // 'maxLength', // TODO proper fix for CL-5456 need to use FormControl as root element for FormField
            'hideCreatedOption',
            'minLength',
            'valueFormatter',
            'formatErrorMessage',
            'maxValue',
            'minValue',
            'decimalScale',
            'creatable',
            'options',
          ]),
        }
      : undefined,
    {
      component: () => <Divider />,
      name: 'divider',
      indent: 1,
    },
    {component: FormRadio, name: 'selectedMappingType', key: 1, options: [selectionOptions[1]]},
    !singleValueMapping
      ? {
          component: FormSelect,
          name: 'mappedAttribute',
          indent: 1,
          options: mappedAttributeOptions,
          placeholder: 'Select attribute...',
        }
      : undefined,
    !singleValueMapping
      ? {
          component: FormCheckbox,
          name: 'mapValues',
          disabled: !mappingEnabled,
          indent: 1,
          label: (
            <React.Fragment>
              Map product's attribute values to {marketplaceName} values{' '}
              <TooltipIcon
                title={
                  <React.Fragment>
                    Map your product attribute values to valid {marketplaceName} values. <br />
                    e.g. The ‘Condition' product attribute value, ‘Damaged', was mapped to {marketplaceName}'s 'Used'
                    value. ￼In this case, listings created from products with a 'Damaged' value under the 'Condition'
                    attribute will have their 'Condition' field filled with {marketplaceName}’s valid 'Used' value.
                  </React.Fragment>
                }
                variant="inline"
              />
            </React.Fragment>
          ),
        }
      : undefined,
    ...mapping,
  ];
  return (
    <FormDialog<EditColumnFormData>
      formName={GENERAL_ACTIONS_TYPES.MAP_PRODUCT_PROPERTY}
      title={`Edit ${columnDisplayName} for all listings`}
      validate={null}
      initialValues={initialValues}
      fields={_.filter(fields)}
      fullWidth
      actions={
        <GeneralActions
          types={[GENERAL_ACTIONS_TYPES.MAP_PRODUCT_PROPERTY]}
          displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
          lastActionColor="primary"
          additionalActionData={{propertyName, tableName, options: columnInfo.options}}
        />
      }
      {...props}
    />
  );
}

export default connectWithRouter((state: StoreState, {tableName, propertyName}: Partial<EditColumnDialogProps>) => {
  const props: Partial<EditColumnDialogProps> = getFormValues(
    state,
    'selectedMappingType',
    'mapValues',
    'mappedAttribute'
  );
  const tableState = state.table[tableName];
  props.columnInfo = tableState.columns[propertyName];
  props.data = tableState.data;
  props.currentMapping = tableState.additionalData[propertyName];
  return props;
})(EditColumnDialog) as typeof EditColumnDialog;
